import React, { useRef, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import pipe from 'lodash.flow';
import isString from 'lodash.isstring';
import cx from 'classnames';
import FontFaceObserver from 'fontfaceobserver';
import getTruncatedIndex from './getTruncatedIndex';
import './index.scss';

const PUNCTUATION_REGEX = /([.,/#!?$%^&*;:{}=\-_`~()\][])+$/g;
const READ_MORE = 'Read More';
const READ_LESS = 'Read Less';
const ELLIPSIS = '\u2026';

const ToggleContentType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.bool,
  PropTypes.element,
]);

const getMaxHeight = (lines, lineHeight) => lines * lineHeight;

const toPx = val => `${val}px`;

const getStyles = (ref, lines) => {
  const { lineHeight, ...styles } = window.getComputedStyle(ref.current);
  return {
    lineHeight: parseFloat(lineHeight),
    width: ref.current.clientWidth,
    height: ref.current.scrollHeight,
    font: [
      styles.fontStyle,
      styles.fontVariant,
      `${styles.fontWeight}/${toPx(lineHeight)}`,
      styles.fontSize,
      styles.fontFamily,
    ].join(' '),
    maxHeight: getMaxHeight(lines, parseFloat(lineHeight)),
  };
};

const removePunctuation = word => word.replace(PUNCTUATION_REGEX, '');
const appendTail = tail => word => `${word}${tail}`;

const ToggleButton = ({ children, ...props }) => (
  <button {...props} className="read-more-btn">
    {children}
  </button>
);

ToggleButton.propTypes = {
  open: PropTypes.bool,
  children: PropTypes.node,
};

export const TruncatedText = ({
  text,
  index,
  toggle,
  open,
  expand,
  minimize,
  tail,
}) => {
  if (index < 0) return text;
  if (open)
    return (
      <React.Fragment>
        {text}
        {minimize &&
          React.cloneElement(minimize, { onClick: () => toggle(false) })}
      </React.Fragment>
    );
  const nodes = text ? text.split(' ') : '';
  const visible = nodes?.slice(0, index).join(' ');
  const lastWord = nodes
    ? pipe(removePunctuation, appendTail(tail))(nodes[index])
    : '';
  const hidden = nodes?.slice(index + 1).join(' ');

  return (
    <React.Fragment>
      <span>{visible} </span>
      <span>{lastWord} </span>
      {expand && React.cloneElement(expand, { onClick: () => toggle(true) })}
      <span style={{ display: 'block' }}>{hidden}</span>
    </React.Fragment>
  );
};

TruncatedText.propTypes = {
  text: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  toggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  minimize: ToggleContentType.isRequired,
  expand: ToggleContentType.isRequired,
  tail: PropTypes.string.isRequired,
};

const ReadMore = ({
  children,
  lines,
  animate,
  open,
  toggle,
  expand,
  minimize,
  className,
  tail = ELLIPSIS,
}) => {
  const ref = useRef(null);
  const [{ height, maxHeight, ...styles }, setStyles] = useState({});
  const [fontLoaded, setFontLoaded] = useState(false);

  useLayoutEffect(() => {
    const requestId = window.requestAnimationFrame(() =>
      setStyles(getStyles(ref, lines)),
    );
    return () => window.cancelAnimationFrame(requestId);
  }, [lines, ref]);

  Promise.all([
    new FontFaceObserver('proxima-nova').load(),
    new FontFaceObserver('league-gothic').load(),
  ]).then(() => setFontLoaded(true));

  const truncateIndex = getTruncatedIndex({
    fontLoaded,
    tail,
    expand,
    text: children,
    maxHeight,
    ...styles,
  });

  return (
    <div
      data-testid="container"
      className={cx('read-more', className)}
      style={{
        maxHeight: open ? toPx(height) : toPx(maxHeight),
        transition: animate ? 'max-height 0.5s' : undefined,
        overflowY: open ? 'auto' : undefined,
      }}
      ref={ref}
    >
      <TruncatedText
        open={open}
        toggle={toggle}
        text={children}
        expand={expand}
        minimize={minimize}
        index={truncateIndex}
        tail={tail}
      />
    </div>
  );
};

ReadMore.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  lines: PropTypes.number.isRequired,
  animate: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  expand: ToggleContentType,
  minimize: ToggleContentType,
  tail: PropTypes.string,
};

const formatToggle = (toggle, text) => {
  if (toggle === false) return false;
  if (toggle === undefined) return <ToggleButton>{text}</ToggleButton>;
  if (isString(toggle)) return <ToggleButton>{toggle}</ToggleButton>;
  return toggle;
};

const mapProps = ({ toggleContent = {}, ...props }) => ({
  expand: formatToggle(toggleContent.expand, READ_MORE),
  minimize: formatToggle(toggleContent.minimize, READ_LESS),
  ...props,
});

export default pipe(mapProps, ReadMore);
